import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { rebuildFullOrder } from '../utils/reorderUtils';
import UserAvatar from './UserAvatar';
import './KanbanView.css';

const COLUMNS = [
  { id: 'todo', label: 'To Do' },
  { id: 'in_progress', label: 'In Progress' },
  { id: 'done', label: 'Done' },
];

export default function KanbanView({
  tasks,
  onToggleTask,
  onUpdateTaskMetadata,
  onReorderTasks,
  listId,
  onOpenTaskDrawer,
  isShared = false,
}) {
  const [dragState, setDragState] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  // Card currently hovered as a within-column drop target (insert-before)
  const [dropCardId, setDropCardId] = useState(null);
  // Optimistic overrides: taskId -> { kanban_status, completed }
  // Kept until the tasks prop reflects the change (i.e., server data arrives)
  const [optimisticOverrides, setOptimisticOverrides] = useState({});
  const prevTasksRef = useRef(tasks);

  // Clear optimistic overrides once the tasks prop updates with fresh data
  useEffect(() => {
    if (tasks !== prevTasksRef.current) {
      prevTasksRef.current = tasks;
      setOptimisticOverrides(prev => {
        if (Object.keys(prev).length === 0) return prev;
        // Clear overrides for tasks whose server data now matches the override
        const next = {};
        for (const [taskId, override] of Object.entries(prev)) {
          const task = tasks.find(t => t.id === taskId);
          if (!task) continue;
          const serverStatus = task.metadata?.kanban_status || 'todo';
          const serverCompleted = task.completed;
          // Keep override only if server hasn't caught up yet
          if (serverStatus !== override.kanban_status || serverCompleted !== override.completed) {
            next[taskId] = override;
          }
        }
        return Object.keys(next).length === 0 ? {} : next;
      });
    }
  }, [tasks]);

  const columns = useMemo(() => {
    const todo = [];
    const inProgress = [];
    const done = [];

    for (const task of tasks) {
      const override = optimisticOverrides[task.id];
      const completed = override ? override.completed : task.completed;
      const kanbanStatus = override
        ? override.kanban_status
        : task.metadata?.kanban_status || 'todo';

      if (completed || kanbanStatus === 'done') {
        done.push(task);
      } else if (kanbanStatus === 'in_progress') {
        inProgress.push(task);
      } else {
        todo.push(task);
      }
    }

    return { todo, in_progress: inProgress, done };
  }, [tasks, optimisticOverrides]);

  const handleDragStart = useCallback((e, taskId, sourceColumn) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', taskId);
    setDragState({ taskId, sourceColumn });
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleDragEnter = useCallback((columnId) => {
    setDropTarget(columnId);
  }, []);

  const handleDragLeave = useCallback((e, columnId) => {
    // Only clear if leaving the column entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDropTarget((prev) => (prev === columnId ? null : prev));
    }
  }, []);

  const handleDragEnd = useCallback(() => {
    setDragState(null);
    setDropTarget(null);
    setDropCardId(null);
  }, []);

  // Apply the status/completion change for a cross-column move (optimistic +
  // background API). Shared by column-body drops and card drops.
  const applyColumnChange = useCallback(
    (taskId, sourceColumn, targetColumn) => {
      setOptimisticOverrides((prev) => ({
        ...prev,
        [taskId]: {
          kanban_status: targetColumn,
          completed: targetColumn === 'done',
        },
      }));

      if (targetColumn === 'done' && sourceColumn !== 'done') {
        // Moving to Done — toggle complete (backend syncs kanban_status)
        onToggleTask(listId, taskId).catch(err =>
          console.error('Error moving task to done:', err)
        );
      } else if (sourceColumn === 'done' && targetColumn !== 'done') {
        // Moving from Done — uncomplete, then set target status
        onToggleTask(listId, taskId)
          .then(() => {
            if (targetColumn === 'in_progress') {
              return onUpdateTaskMetadata(taskId, { kanban_status: 'in_progress' });
            }
          })
          .catch(err => console.error('Error moving task from done:', err));
      } else {
        // Moving between To Do and In Progress
        onUpdateTaskMetadata(taskId, { kanban_status: targetColumn })
          .catch(err => console.error('Error updating kanban status:', err));
      }
    },
    [listId, onToggleTask, onUpdateTaskMetadata]
  );

  // Drop on the column body (empty space / below cards): change status only.
  const handleDrop = useCallback(
    (e, targetColumn) => {
      e.preventDefault();
      const taskId = e.dataTransfer.getData('text/plain');
      setDragState(null);
      setDropTarget(null);
      setDropCardId(null);

      if (!taskId || !dragState || dragState.sourceColumn === targetColumn) {
        return;
      }

      applyColumnChange(taskId, dragState.sourceColumn, targetColumn);
    },
    [dragState, applyColumnChange]
  );

  // Drop onto a specific card: insert before that card within the column, and
  // (for cross-column drops) also change status. Persists the new global order.
  const handleCardDrop = useCallback(
    (e, targetTaskId, targetColumn) => {
      e.preventDefault();
      e.stopPropagation();
      const taskId = e.dataTransfer.getData('text/plain');
      const sourceColumn = dragState?.sourceColumn;
      setDragState(null);
      setDropTarget(null);
      setDropCardId(null);

      if (!taskId || !dragState || taskId === targetTaskId) {
        return;
      }

      const crossColumn = sourceColumn !== targetColumn;
      if (crossColumn) {
        applyColumnChange(taskId, sourceColumn, targetColumn);
      }

      if (!onReorderTasks) return;

      const ids = (columns[targetColumn] || []).map((t) => t.id);
      let newColumnOrder;
      let affectedIds;

      if (crossColumn) {
        let insertAt = ids.indexOf(targetTaskId);
        if (insertAt < 0) insertAt = ids.length;
        newColumnOrder = [...ids.slice(0, insertAt), taskId, ...ids.slice(insertAt)];
        affectedIds = new Set([...ids, taskId]);
      } else {
        const fromIndex = ids.indexOf(taskId);
        const toIndex = ids.indexOf(targetTaskId);
        if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
          return;
        }
        newColumnOrder = [...ids];
        const [moved] = newColumnOrder.splice(fromIndex, 1);
        newColumnOrder.splice(toIndex, 0, moved);
        affectedIds = new Set(ids);
      }

      onReorderTasks(listId, rebuildFullOrder(tasks, affectedIds, newColumnOrder));
    },
    [dragState, columns, tasks, listId, onReorderTasks, applyColumnChange]
  );

  const handleCardDragOver = useCallback((e, taskId) => {
    e.preventDefault();
    e.stopPropagation();
    setDropCardId(taskId);
  }, []);

  return (
    <div className="kanban-container">
      {COLUMNS.map((col) => {
        const columnTasks = columns[col.id] || [];
        const isDropActive =
          dropTarget === col.id &&
          dragState &&
          dragState.sourceColumn !== col.id;

        return (
          <div
            key={col.id}
            className={`kanban-column kanban-column-${col.id.replace('_', '-')}${
              isDropActive ? ' kanban-column-drop-active' : ''
            }`}
            onDragOver={handleDragOver}
            onDragEnter={() => handleDragEnter(col.id)}
            onDragLeave={(e) => handleDragLeave(e, col.id)}
            onDrop={(e) => handleDrop(e, col.id)}
          >
            <div className="kanban-column-header">
              <span>{col.label}</span>
              <span className="kanban-column-count">{columnTasks.length}</span>
            </div>
            <div className="kanban-column-body">
              {columnTasks.length === 0 ? (
                <div className="kanban-empty-column">No tasks</div>
              ) : (
                columnTasks.map((task) => (
                  <div
                    key={task.id}
                    className={`kanban-card${
                      dragState?.taskId === task.id ? ' kanban-card-dragging' : ''
                    }${col.id === 'done' ? ' kanban-card-completed' : ''}${
                      dragState && dropCardId === task.id && dragState.taskId !== task.id
                        ? ' kanban-card-drop-target'
                        : ''
                    }`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, task.id, col.id)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleCardDragOver(e, task.id)}
                    onDrop={(e) => handleCardDrop(e, task.id, col.id)}
                    onClick={() =>
                      onOpenTaskDrawer && onOpenTaskDrawer(task)
                    }
                  >
                    <div className="kanban-card-text">
                      <span>
                        {task.text}
                        {task.hasNotes && (
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            className="kanban-note-icon"
                            aria-label="Has notes"
                            title="This task has notes"
                          >
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                            <line x1="16" y1="13" x2="8" y2="13" />
                            <line x1="16" y1="17" x2="8" y2="17" />
                            <polyline points="10 9 9 9 8 9" />
                          </svg>
                        )}
                      </span>
                      {isShared && task.assignedTo && task.assignedTo.name && (
                        <UserAvatar user={task.assignedTo} size="small" className="kanban-assignee-avatar" label="Assigned to" />
                      )}
                    </div>
                    {task.subTasksCount > 0 && (
                      <div className="kanban-card-meta">
                        <div className="kanban-subtask-progress">
                          <div className="kanban-subtask-bar">
                            <div
                              className="kanban-subtask-bar-fill"
                              style={{
                                width: `${
                                  (task.completedSubTasksCount /
                                    task.subTasksCount) *
                                  100
                                }%`,
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
